import { type ReactNode, use } from "react";
import { Button } from "@/components/ui/button";
import { renamesOf } from "@/lib/old-names";
import { TerminologyContext } from "../terms/Terminology";
import { Checkbox } from "@/components/ui/checkbox";
import { Panel } from "@/components/ui/Panel";
import { Plus, Undo2 } from "lucide-react";
import type { FigureResponse, PointModel } from "@patent-studio/api-types";
import { cn } from "@/lib/utils";
import { DEFINE_COLUMN_BOUND } from "./define-layout";
import type { Definitions } from "@/lib/element-registry";
import type { MarkEdits } from "./AnnotationCard";
import { AnnotationCard } from "./AnnotationCard";
import type { Placement } from "./AnnotatedDrawing";
import { WalkthroughCard, type WalkthroughClip } from "./WalkthroughCard";
import { BriefDescriptionCard } from "./BriefDescriptionCard";
import type { FigureBrief } from "@/hooks/use-figures";
import { DeleteFigurePopover, figureName } from "./DeletedFigures";
import { ElementRegistryCard } from "./ElementRegistryCard";
import { FigureDrawing } from "./FigureDrawing";
import {
  FullscreenPanel,
  FullscreenToggle,
  useFullscreenFigure,
} from "./FigureFullscreen";

/**
 * The figure's name, the control that turns its numbering on or off, and the way to take the
 * drawing out of the set.
 *
 * The numbering control lives here, in the figure's own header, rather than inside whichever
 * branch is showing. Both branches would otherwise put it somewhere different, and a control
 * that moves when it is used is one the attorney has to find again each time. Beside the
 * filename rather than pushed to the far edge, where it read as belonging to the page instead
 * of to this drawing.
 *
 * Every control in the row acts on this drawing, so the delete can hold the far edge: it is
 * the one that ends the figure rather than changes it, and the distance is what keeps it from
 * being reached on the way to the others.
 */
function FigureHeading({
  figure,
  removable,
  priorArt,
  onRemoveNumbering,
  onPriorArt,
  onDelete,
}: {
  figure: FigureResponse;
  removable: boolean;
  priorArt: boolean;
  onRemoveNumbering: () => void;
  onPriorArt: (priorArt: boolean) => void;
  onDelete: () => void;
}) {
  const priorArtId = `prior-art-${figure.file_id}`;
  return (
    <div
      data-testid="define-figure-header"
      className="mb-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-2"
    >
      <h2 className="text-lg font-semibold tracking-tight">
        {`FIG. ${figure.label ?? ""}`}
      </h2>
      <span className="font-mono text-[11.5px] text-muted-foreground">
        {figure.original_filename}
      </span>
      {removable && (
        <Button
          variant="quiet"
          size="dense"
          type="button"
          onClick={onRemoveNumbering}
          className="ml-1"
        >
          <Undo2 aria-hidden />
          Remove numbered components
        </Button>
      )}
      {/* A drawing of the prior art carries the legend "Prior Art" on its sheet. */}
      <span className="ml-1 flex items-center gap-1.5">
        <Checkbox
          id={priorArtId}
          checked={priorArt}
          onCheckedChange={(state) => {
            onPriorArt(state === true);
          }}
        />
        <label htmlFor={priorArtId} className="text-sm">
          Prior art
        </label>
      </span>
      <DeleteFigurePopover
        name={figureName(figure.label, figure.original_filename)}
        onConfirm={onDelete}
      />
    </div>
  );
}

/** A drawing carrying no numbered components: the drawing, and at its corner the way to number it. */
function DescriptionOnly({
  figure,
  onAddNumbering,
}: {
  figure: FigureResponse;
  onAddNumbering: () => void;
}) {
  return (
    <Panel
      data-testid="define-drawing-card"
      className="max-w-[420px] shadow-sm"
    >
      <div className="mb-3 flex justify-end">
        <Button
          variant="primary"
          size="dense"
          type="button"
          onClick={onAddNumbering}
        >
          <Plus aria-hidden />
          Add numbered components
        </Button>
      </div>
      <FigureDrawing
        thumbnailUrl={figure.thumbnail_url}
        filename={figure.original_filename}
      />
    </Panel>
  );
}

/**
 * The element registry's column. Bounded at panel size, so a long registry ends above the
 * walk-through; the card shrinks to the bound and scrolls its rows, so its border still closes.
 */
function ElementColumn({
  fullscreen,
  children,
}: {
  fullscreen: boolean;
  children: ReactNode;
}) {
  return (
    <div
      data-testid="define-element-column"
      className={cn(
        "flex min-h-0 flex-col",
        !fullscreen && DEFINE_COLUMN_BOUND,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The drawing with its tools and its registry, at panel size or filling the screen.
 *
 * Full screen lives here rather than on the surface above because only this branch has an
 * annotation surface to expand: an unnumbered figure has nothing the toggle could
 * act on. It is view state and is never written down, so entering and leaving issue
 * nothing to the store.
 */
function NumberedSurface({
  figure,
  figures,
  definitions,
  assigned,
  selected,
  onSelect,
  onPlace,
  onRemovePin,
  onRetitle,
  servedTitles,
  onConfirm,
  onConfirmAll,
  onReuse,
  onNest,
  onTakeOut,
  edits,
}: DefineSurfaceProps) {
  const { fullscreen, open, leave, toggleRef } = useFullscreenFigure();
  return (
    <FullscreenPanel
      open={fullscreen}
      heading={`FIG. ${figure.label ?? ""}`}
      filename={figure.original_filename}
      onExit={leave}
    >
      <div
        className={cn(
          "grid gap-3.5",
          fullscreen
            ? // The row is bounded as well as the grid, or it grows to the drawing's own
              // height, the frame never has a height to scroll inside, and the column
              // simply overflows the room it was given.
              "h-full min-h-0 grid-rows-[minmax(0,1fr)] items-stretch lg:grid-cols-[minmax(0,1fr)_minmax(280px,320px)]"
            : "items-start lg:grid-cols-[minmax(420px,1.15fr)_minmax(360px,1fr)]",
        )}
      >
        <AnnotationCard
          thumbnailUrl={figure.thumbnail_url}
          filename={figure.original_filename}
          definitions={definitions}
          assigned={assigned}
          fileId={figure.file_id}
          figures={figures}
          selected={selected}
          onSelect={onSelect}
          onPlace={onPlace}
          onRemove={onRemovePin}
          edits={edits}
          fills={fullscreen}
          // Off the screen once the panel is up: the title bar's close is the way out,
          // and a second exit over the drawing would compete with it.
          overlay={
            fullscreen ? undefined : (
              <FullscreenToggle ref={toggleRef} onOpen={open} />
            )
          }
        />
        <ElementColumn fullscreen={fullscreen}>
          <ElementRegistryCard
            definitions={definitions}
            assigned={assigned}
            fileId={figure.file_id}
            figures={figures}
            selected={selected}
            onRetitle={onRetitle}
            servedTitles={servedTitles}
            onConfirm={onConfirm}
            onConfirmAll={onConfirmAll}
            onReuse={onReuse}
            onNest={onNest}
            onTakeOut={onTakeOut}
          />
        </ElementColumn>
      </div>
    </FullscreenPanel>
  );
}

/**
 * One drawing's Define surface: the drawing with whatever its numbering
 * setting says belongs beside it, and the walk-through on the row under them.
 *
 * The walk-through comes after the drawing because that is the order the
 * attorney works in: look, then say what is seen. Which branch renders above
 * it is the figure's `numbered` flag alone, never its status: a walked-through
 * figure is complete rather than not started, so reading the status here would
 * take the add control away from the drawings most likely to need it.
 */
interface DefineSurfaceProps {
  figure: FigureResponse;
  /** Every drawing on the task, so an element may be borrowed from another one. */
  figures: FigureResponse[];
  definitions: Definitions;
  /** The number each element shows, projected once by the pane above. */
  assigned: Map<string, string>;
  numbered: boolean;
  /** Whether the drawing shows the prior art, so its sheet carries the legend "Prior Art". */
  priorArt: boolean;
  onPriorArt: (priorArt: boolean) => void;
  /** Whether numbering may be taken back off, which it may while nothing is pinned. */
  removable: boolean;
  walkthrough: string;
  /** The brief description the draft prints for this figure. */
  description: string;
  onDescription: (description: string) => Promise<void>;
  /** Auto-describe for this figure: whether Praxis is writing, why it last failed, and the ask. */
  brief: FigureBrief;
  /** The title of the group the figure sits in, if it sits in one. */
  groupTitle: string | null;
  clips: readonly WalkthroughClip[];
  selected: Placement | undefined;
  onWalkthrough: (walkthrough: string) => Promise<void>;
  onUploadClip: (file: File) => void;
  onRecordClip: (file: File) => void;
  onRetryClip: (fileId: string) => void;
  onAdoptTranscript: (clipId: string, transcript: string) => void;
  walkthroughAccepted: boolean;
  /** What took the confirmation off, when a change other than Amend did. */
  walkthroughLifted?: string | null;
  onAccept: (walkthrough: string) => void;
  onAmend: () => void;
  onAddNumbering: () => void;
  onRemoveNumbering: () => void;
  onSelect: (placement: Placement | undefined) => void;
  onPlace: (
    pin: PointModel,
    tip: PointModel,
    elementId: string | undefined,
  ) => void;
  onRemovePin: (placement: Placement) => void;
  onRetitle: (elementId: string, title: string) => Promise<void>;
  /** Each component's title as the store has acknowledged it, which the title fields save against. */
  servedTitles: ReadonlyMap<string, string>;
  onConfirm: (elementId: string) => void;
  onConfirmAll: () => void;
  onReuse: (fromElementId: string, toElementId: string) => void;
  onNest: (dragged: string, target: string) => void;
  onTakeOut: (elementId: string) => void;
  onDelete: () => void;
  edits: MarkEdits;
}

/** What the attorney writes about the drawing: the walk-through, then the brief description. */
function SurfaceCards({
  figure,
  walkthrough,
  clips,
  onWalkthrough,
  onUploadClip,
  onRecordClip,
  onRetryClip,
  onAdoptTranscript,
  walkthroughAccepted,
  walkthroughLifted = null,
  onAccept,
  onAmend,
  description,
  onDescription,
  brief,
  groupTitle,
}: DefineSurfaceProps) {
  // The components renamed since the walk-through was written, from the terms' history.
  const renames = renamesOf(use(TerminologyContext)?.view);
  return (
    <>
      {/* Keyed by figure so moving between drawings starts a new field rather
          than carrying a half-typed draft across. Qualified by what it keys,
          because the brief card beside it is keyed by the same drawing and
          siblings may not share a key. */}
      <WalkthroughCard
        key={`walkthrough-${figure.file_id}`}
        walkthrough={walkthrough}
        clips={clips}
        onSave={onWalkthrough}
        onUpload={onUploadClip}
        onRecord={onRecordClip}
        onRetryClip={onRetryClip}
        onAdoptTranscript={onAdoptTranscript}
        accepted={walkthroughAccepted}
        lifted={walkthroughLifted}
        onAccept={onAccept}
        onAmend={onAmend}
        renames={renames}
      />
      <BriefDescriptionCard
        key={`brief-${figure.file_id}`}
        description={description}
        groupTitle={groupTitle}
        onSave={onDescription}
        canDescribe={walkthrough.trim() !== ""}
        brief={brief}
      />
    </>
  );
}

export function FigureDefineSurface(props: DefineSurfaceProps) {
  // Named where this component uses them, and handed on whole to the branch that uses the
  // rest: every one of the annotation props travels to the numbered branch unchanged, and
  // listing them twice more only means three lists to keep in step.
  const {
    figure,
    numbered,
    priorArt,
    onPriorArt,
    removable,
    onAddNumbering,
    onRemoveNumbering,
    onDelete,
  } = props;
  return (
    <>
      {/* Two rows of the columns' grid: the heading on the row the figure list's head
          shares, the drawing on the row the list shares, so all three cards start level. One
          column held to the surface's width, so a long file name wraps and never widens it. */}
      <section
        data-testid="define-surface"
        data-file-id={figure.file_id}
        className="lg:row-span-2 lg:grid lg:grid-cols-[minmax(0,1fr)] lg:grid-rows-subgrid"
      >
        <FigureHeading
          figure={figure}
          removable={removable}
          priorArt={priorArt}
          onRemoveNumbering={onRemoveNumbering}
          onPriorArt={onPriorArt}
          onDelete={onDelete}
        />

        {numbered ? (
          // Keyed by figure, so a placement half made on one drawing is not a placement on
          // the next and the next drawing opens at panel size rather than inheriting how the
          // last one was being looked at. Carrying either across would put the second
          // drawing in a state the attorney never asked for.
          <NumberedSurface key={`numbered-${figure.file_id}`} {...props} />
        ) : (
          <DescriptionOnly figure={figure} onAddNumbering={onAddNumbering} />
        )}
      </section>
      {/* Under the figures, the drawing and the elements, across the page: the boxes the
          attorney writes in take the outer width. */}
      <div data-testid="define-cards" className="lg:col-span-2">
        <SurfaceCards {...props} />
      </div>
    </>
  );
}
